import React from 'react';
import './Invoice.css';

// Minimal types for clarity
interface PriceTier {
  minQty: number;
  maxQty?: number;
  price: number;
}

interface CartSizeItem {
  size: string;
  quantity: number;
  cartItemId?: string | number;
}

interface Product {
  id: number;
  name: string;
  priceTiers: PriceTier[];
  cartSizes?: CartSizeItem[];
}

interface CartItem {
  id: number | string;
  product?: Product;
  quantity?: number;
  size?: string | null;
  price?: number;
  total?: number;
  cartSizes?: CartSizeItem[];
  cartQuantity?: number;
  cartPrice?: number;
  cartTotal?: number;
}

interface InvoiceProps {
  items: CartItem[];
}

interface InvoiceGroup {
  product: Product;
  sizes: Array<CartSizeItem & { price: number; total: number }>;
  totalQuantity: number;
  totalAmount: number;
}

const Invoice: React.FC<InvoiceProps> = ({ items }) => {
  const normalizeInvoiceItems = (rawItems: CartItem[]): InvoiceGroup[] => {
    const grouped = new Map<string | number, InvoiceGroup>();

    rawItems.forEach((rawItem) => {
      const hasNestedProduct = rawItem.product !== undefined && rawItem.product !== null;
      const product = hasNestedProduct ? rawItem.product! : (rawItem as unknown as Product);
      
      if (!product?.id) {
        console.warn('Invoice: Invalid product, skipping item', rawItem);
        return;
      }

      const productKey = product.id;
      
      // Priority: cartSizes array > individual size property > "N/A"
      let sizesToProcess: Array<{ size: string; quantity: number; cartItemId?: string | number }> = [];
      
      if (rawItem.cartSizes && Array.isArray(rawItem.cartSizes) && rawItem.cartSizes.length > 0) {
        // Use grouped cartSizes
        sizesToProcess = rawItem.cartSizes.filter(cs => cs.quantity > 0);
      } else if (rawItem.size) {
        // Use individual size from item
        sizesToProcess = [{ size: rawItem.size, quantity: rawItem.quantity ?? 1, cartItemId: rawItem.id }];
      } else {
        // No size - create single "N/A" entry
        sizesToProcess = [{ size: 'N/A', quantity: rawItem.quantity ?? rawItem.cartQuantity ?? 1, cartItemId: rawItem.id }];
      }

      const unitPrice = rawItem.price ?? rawItem.cartPrice ?? 0;

      let existingGroup = grouped.get(productKey);
      if (!existingGroup) {
        const sizes = sizesToProcess.map((sizeItem) => ({
          size: sizeItem.size,
          quantity: sizeItem.quantity,
          cartItemId: sizeItem.cartItemId,
          price: unitPrice,
          total: unitPrice * sizeItem.quantity,
        }));

        grouped.set(productKey, {
          product,
          sizes,
          totalQuantity: sizes.reduce((sum, s) => sum + s.quantity, 0),
          totalAmount: sizes.reduce((sum, s) => sum + s.total, 0),
        });
      } else {
        // Merge with existing group
        sizesToProcess.forEach((newSize) => {
          const existingSize = existingGroup!.sizes.find((s) => s.size === newSize.size);
          if (existingSize) {
            existingSize.quantity += newSize.quantity;
            existingSize.total = existingSize.price * existingSize.quantity;
          } else {
            existingGroup!.sizes.push({
              size: newSize.size,
              quantity: newSize.quantity,
              cartItemId: newSize.cartItemId,
              price: unitPrice,
              total: unitPrice * newSize.quantity,
            });
          }
        });

        existingGroup.totalQuantity = existingGroup.sizes.reduce((sum, s) => sum + s.quantity, 0);
        existingGroup.totalAmount = existingGroup.sizes.reduce((sum, s) => sum + s.total, 0);
      }
    });

    return Array.from(grouped.values());
  };

  // Handle empty state
  if (!items || items.length === 0) {
    return <p className="empty-text">No items found in your selection.</p>;
  }

  const invoiceGroups = normalizeInvoiceItems(items);
  const grandTotal = invoiceGroups.reduce((acc, group) => acc + group.totalAmount, 0);

  return (
    <ul className="responsive-table">
        {/* Table Header */}
        <li className="table-header">
          <div className="col col-1">S.No</div>
          <div className="col col-2">Product</div>
          <div className="col col-3">Size</div>
          <div className="col col-4">Quantity</div>
          <div className="col col-5">Price/Unit</div>
          <div className="col col-6">Total</div>
        </li>

        {invoiceGroups.map((group, groupIndex) => (
          group.sizes.map((sizeItem, sizeIndex) => (
            <li key={`${group.product.id}-${sizeItem.size}-${sizeIndex}`} className="table-row">
              <div className="col col-1" data-label="S.No:">
                {groupIndex + 1}.{sizeIndex + 1}
              </div>
              <div className="col col-2" data-label="Product:">
                <span className="product-name">{group.product.name}</span>
              </div>
              <div className="col col-3" data-label="Size:">
                <span className={`size-badge ${sizeItem.size === 'N/A' ? 'no-size' : ''}`}>
                  {sizeItem.size}
                </span>
              </div>
              <div className="col col-4" data-label="Quantity:">
                {sizeItem.quantity}
              </div>
              <div className="col col-5" data-label="Price/Unit:">
                ₹{sizeItem.price.toLocaleString('en-IN')}
              </div>
              <div className="col col-6" data-label="Total:">
                ₹{sizeItem.total.toLocaleString('en-IN')}
              </div>
            </li>
          ))
        ))}

        {/* Grand Total Row */}
        <li className="table-row total-row">
          <div className="col col-1"></div>
          <div className="col col-2">
            <strong>Grand Total</strong>
          </div>
          <div className="col col-3"></div>
          <div className="col col-4"></div>
          <div className="col col-5"></div>
          <div className="col col-6" data-label="Grand Total:">
            <strong>₹{grandTotal.toLocaleString('en-IN')}</strong>
          </div>
        </li>
      </ul>
    );
};

export default Invoice;